import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePremisesStore, useOrgUsersStore } from "../../../store/store";
import { PageLoader } from "../../../components/loader/Loader";
import { useAuth } from "../../../auth/AuthProvider";
import axiosInstance from "../../../common/axiosInstance";
import { toast } from "react-toastify";

interface ChecklistItem {
  id: string;
  question: string;
  weightage?: number;
  categoryId: string;
}

interface Checklist {
  _id: string;
  name: string;
  premisesId: string;
  materialType?: string;
  items: ChecklistItem[];
}

interface InspectionResponse {
  itemId: string;
  question: string;
  complianceStatus:
    | "complied"
    | "partially_complied"
    | "not_complied"
    | "not_applicable";
  reason?: string;
}

interface Inspection {
  _id: string;
  checklistId: string;
  premisesId: string;
  inspectionDate: string;
  status: "scheduled" | "pending" | "completed";
  assignedTo: string;
  remarks?: string;
  responses?: InspectionResponse[];
  checklist?: Checklist;
}

const COMPLIANCE_OPTIONS = [
  { value: "complied", label: "Complied", color: "bg-green-500" },
  {
    value: "partially_complied",
    label: "Partially Complied",
    color: "bg-yellow-500",
  },
  { value: "not_complied", label: "Not Complied", color: "bg-red-500" },
  { value: "not_applicable", label: "Not Applicable", color: "bg-gray-500" },
];

const HazMatInspectionDetail: React.FC = () => {
  const { inspectionId } = useParams<{ inspectionId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { allPremises } = usePremisesStore();
  const { orgUsersData } = useOrgUsersStore();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [responses, setResponses] = useState<
    Record<string, InspectionResponse>
  >({});
  const [remarks, setRemarks] = useState("");

  const isAssigned = user?.id === inspection?.assignedTo;
  const canEdit = isAssigned && inspection?.status !== "completed";

  useEffect(() => {
    if (inspectionId) {
      fetchInspection();
    }
  }, [inspectionId]);

  const fetchInspection = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/hazmat-inspections/${inspectionId}`);

      if (response.status === 200) {
        const data = response.data.data;
        setInspection(data);
        setRemarks(data.remarks || "");

        // Load checklist
        if (data.checklistId) {
          const checklistResponse = await axiosInstance.get(`/api/hazmat-checklist-config/${data.checklistId}`);
          if (checklistResponse.status === 200) {
            setChecklist(checklistResponse.data.data);
          }
        }

        // Initialize responses
        if (data.responses && Array.isArray(data.responses)) {
          const responsesMap: Record<string, InspectionResponse> = {};
          data.responses.forEach((resp: InspectionResponse) => {
            responsesMap[resp.itemId] = resp;
          });
          setResponses(responsesMap);
        } else if (checklist) {
          // Initialize empty responses for all checklist items
          const initialResponses: Record<string, InspectionResponse> = {};
          checklist.items.forEach((item) => {
            initialResponses[item.id] = {
              itemId: item.id,
              question: item.question,
              complianceStatus: "not_applicable",
            };
          });
          setResponses(initialResponses);
        }
      }
    } catch (error) {
      console.error("Error fetching inspection:", error);
      toast.error("Failed to load inspection");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (checklist && Object.keys(responses).length === 0) {
      // Initialize empty responses for all checklist items
      const initialResponses: Record<string, InspectionResponse> = {};
      checklist.items.forEach((item) => {
        initialResponses[item.id] = {
          itemId: item.id,
          question: item.question,
          complianceStatus: "not_applicable",
        };
      });
      setResponses(initialResponses);
    }
  }, [checklist]);

  const handleComplianceChange = (
    itemId: string,
    question: string,
    status: InspectionResponse["complianceStatus"]
  ) => {
    if (!canEdit) return;

    setResponses((prev) => {
      const updated = { ...prev };
      updated[itemId] = {
        itemId,
        question,
        complianceStatus: status,
        reason: status === "not_complied" ? updated[itemId]?.reason : undefined,
      };
      return updated;
    });
  };

  const handleReasonChange = (itemId: string, reason: string) => {
    if (!canEdit) return;

    setResponses((prev) => {
      const updated = { ...prev };
      if (updated[itemId]) {
        updated[itemId] = {
          ...updated[itemId],
          reason,
        };
      }
      return updated;
    });
  };

  const validateResponses = (): boolean => {
    if (!checklist) return false;

    for (const item of checklist.items) {
      const response = responses[item.id];
      if (!response || !response.complianceStatus) {
        toast.error(`Please select compliance status for: ${item.question}`);
        return false;
      }

      if (response.complianceStatus === "not_complied") {
        if (!response.reason || !response.reason.trim()) {
          toast.error(
            `Please provide a reason for non-compliance: ${item.question}`
          );
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;

    if (!validateResponses()) return;

    try {
      setSubmitting(true);
      const response = await axiosInstance.post(`/api/hazmat-inspections/${inspectionId}/submit`, {
          responses: Object.values(responses),
          remarks,
        });

      if (response.status === 200) {
        toast.success("Inspection submitted successfully");
        setTimeout(() => {
          navigate("/hazardous-materials/inspections");
        }, 1500);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to submit inspection");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <PageLoader />
      </div>
    );
  }

  if (!inspection || !checklist) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-gray-500">Inspection not found</p>
      </div>
    );
  }

  const premise = allPremises.find((p) => p.id === inspection.premisesId);
  const assignedUser = orgUsersData.find(
    (u: any) => u._id === inspection.assignedTo
  );

  return (
    <div className="px-2 py-4 max-w-7xl mx-auto mt-14 ml-2 min-h-100vh">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
          Hazardous Material Inspection
        </h2>
        <button
          className="flex items-center justify-center font-semibold gap-2 bg-white text-[#0fa9a6] !border !border-[#0fa9a6] px-4 py-2 rounded-full hover:!bg-[#0fa9a6] hover:!border-teal-700 hover:text-white transition"
          onClick={() => navigate("/hazardous-materials/inspections")}
        >
          BACK
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Inspection Information */}
        <section className="mb-6">
          <div className="bg-gray-50 border-t-4 border-teal-400 shadow rounded p-4 flex flex-col sm:flex-row items-center justify-between gap-6 sm:gap-0">
            <div className="flex-1 flex flex-col items-center sm:items-start text-center sm:text-left">
              <div className="text-xs uppercase text-gray-600 mb-1">
                Inspection Date
              </div>
              <div className="text-lg text-gray-800 font-semibold">
                {inspection.inspectionDate
                  ? new Date(inspection.inspectionDate).toLocaleDateString()
                  : "--"}
              </div>
            </div>

            <div className="hidden sm:block h-10 w-px bg-gray-200 mx-4" />

            <div className="flex-1 flex flex-col items-center sm:items-start text-center sm:text-left">
              <div className="text-xs uppercase text-gray-600 mb-1">
                Premises
              </div>
              <div className="text-lg text-gray-800 font-semibold">
                {premise?.premisesName || "--"}
              </div>
            </div>

            <div className="hidden sm:block h-10 w-px bg-gray-200 mx-4" />

            <div className="flex-1 flex flex-col items-center sm:items-start text-center sm:text-left">
              <div className="text-xs uppercase text-gray-600 mb-1">
                Assigned To
              </div>
              <div className="text-lg text-gray-800 font-semibold">
                {assignedUser
                  ? `${assignedUser.firstName} ${assignedUser.lastName}`
                  : "--"}
              </div>
            </div>

            <div className="hidden sm:block h-10 w-px bg-gray-200 mx-4" />

            <div className="flex-1 flex flex-col items-center sm:items-start text-center sm:text-left">
              <div className="text-xs uppercase text-gray-600 mb-1">Status</div>
              <div className="text-lg text-gray-800 font-semibold">
                {inspection.status || "--"}
              </div>
            </div>
          </div>
        </section>

        {/* Checklist */}
        <section className="card p-6 mb-6 shadow-sm rounded-lg bg-white">
          <h2 className="font-semibold text-lg mb-6 border-b border-gray-200 pb-2">
            Checklist: {checklist.name}
          </h2>
          <div className="space-y-6">
            {checklist.items.map((item) => {
              const response = responses[item.id];
              const selectedStatus =
                response?.complianceStatus || "not_applicable";
              const showReason = selectedStatus === "not_complied";

              return (
                <div
                  key={item.id}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800 text-base mb-1">
                        {item.question}
                      </h4>
                      {item.weightage && (
                        <p className="text-sm text-gray-500">
                          Weightage: {item.weightage}%
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    {COMPLIANCE_OPTIONS.map((option) => {
                      const isSelected = selectedStatus === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          disabled={!canEdit}
                          className={`px-5 py-2 rounded-md font-semibold transition-colors duration-200 ${
                            isSelected
                              ? `${option.color} text-white`
                              : "bg-white text-gray-600 border border-gray-300 hover:bg-gray-100"
                          } ${!canEdit ? "opacity-50 cursor-not-allowed" : ""}`}
                          onClick={() =>
                            handleComplianceChange(
                              item.id,
                              item.question,
                              option.value as InspectionResponse["complianceStatus"]
                            )
                          }
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>

                  {showReason && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium mb-1 text-red-600">
                        Reason for Non-Compliance{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        className="border border-red-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="Enter reason for non-compliance"
                        value={response?.reason || ""}
                        onChange={(e) =>
                          handleReasonChange(item.id, e.target.value)
                        }
                        required
                        disabled={!canEdit}
                        rows={2}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Remarks */}
        <section className="card p-4 mb-6">
          <h2 className="font-semibold text-lg mb-2">Remarks</h2>
          <textarea
            rows={4}
            disabled={!canEdit}
            className="form-control min-h-[100px] border border-gray-300 rounded p-2 w-full"
            placeholder="Add any additional notes or observations..."
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </section>

        {/* Submit Button */}
        {canEdit && (
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="btn btn-primary roundedBt px-8 py-2 text-white text-base font-semibold"
            >
              {submitting ? "Submitting..." : "Submit Inspection"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default HazMatInspectionDetail;
